import { AppConfigErrorBanner } from '@/components/app-config-error-banner';
import { ThemedText } from '@/components/themed-text';
import { SeoHead, SITE_URL } from '@/components/seo-head';
import { SemanticTable } from '@/components/seo/semantic-table';
import { SiteFooter } from '@/components/site/site-footer';
import { SiteHeader } from '@/components/site/site-header';
import { Badge, Button, Card, FadeInView, PressableScale, SlideInView } from '@/components/ui';
import { Ionicons } from '@expo/vector-icons';
import { Href, Link, router, Stack } from 'expo-router';
import React, { useRef, useState, useEffect } from 'react';
import { captureAttributionFromUrl } from '@/lib/attribution';
import api from '@/services/api';
import { useQuery } from '@tanstack/react-query';
import { useTeamPricing } from '@/hooks/use-team-pricing';
import { FALLBACK_TEAM_PRICING, formatCents, quoteTeamPlan } from '@/lib/team-pricing';
import {
  BENEFITS,
  CUSTOM_PLAN_BLOCK,
  FALLBACK_PLANS,
  FAQ_SECTION,
  FEATURES,
  FEATURES_SECTION,
  FINAL_CTA,
  FREE_PLAN_CARD,
  GUIDES_SECTION,
  HOME_FAQ,
  HOME_GUIDE_SLUGS,
  HOME_HERO,
  HOW_IT_WORKS,
  HOW_IT_WORKS_SECTION,
  PANEL_SECTION,
  PLANS_SECTION,
  QUOTE,
  REMINDER_CHAT,
  REMINDER_CHAT_CONTACT,
  SEGMENTS_SECTION,
  TRUST_BADGES,
  WHATSAPP_SECTION,
  faqJsonLd,
  planComparisonTable,
  softwareApplicationJsonLd,
  type ChatMessage,
  type HomeFaqItem,
  type HomePlan,
} from '@/content/marketing/home';
import { GUIDE_PAGES } from '@/content/seo/guides';
import { SEGMENT_PAGES } from '@/content/seo/segments';
import {
  findNodeHandle,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  Pressable,
  ScrollView,
  Text,
  View,
  UIManager,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useThemedHex } from '@/hooks/use-theme-colors';

const isNative = Platform.OS !== 'web';
// Guideline 3.1.1: no iOS a landing não mostra preço fora do StoreKit — a compra
// acontece pela tela de planos, via In-App Purchase.
const hidePlansOnIOS = Platform.OS === 'ios';
const sectionPadding = isNative ? 'relative px-4 py-12' : 'relative px-4 py-20 sm:py-32';
const heroPadding = isNative
  ? 'relative px-4 py-10'
  : 'relative px-4 py-16 sm:py-24 lg:pb-32 lg:pt-12';
const tabularNums: { fontVariant: ('tabular-nums' | 'lining-nums')[] } = {
  fontVariant: ['tabular-nums'],
};

const SOFTWARE_APPLICATION_JSON_LD = softwareApplicationJsonLd(SITE_URL);

// O app do iPhone não mostra preço de assinatura fora do StoreKit.
const FAQ_ITEMS = hidePlansOnIOS ? HOME_FAQ.filter((item) => !item.hasPrice) : HOME_FAQ;

const homeGuides = HOME_GUIDE_SLUGS.map((slug) => GUIDE_PAGES.find((g) => g.slug === slug)).filter(
  (g): g is (typeof GUIDE_PAGES)[number] => Boolean(g),
);

const FaqItem: React.FC<HomeFaqItem> = ({ question, answer, link }) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggle = () => setIsOpen((v) => !v);

  return (
    <Card className="overflow-hidden">
      <Pressable
        onPress={toggle}
        className="w-full flex-row items-center justify-between gap-3 px-5 py-4"
      >
        <ThemedText role="heading" aria-level={3} className="flex-1 font-semibold text-base">
          {question}
        </ThemedText>
        <View className="h-7 w-7 items-center justify-center rounded-full bg-primary-50 dark:bg-primary">
          <Ionicons
            className="text-primary dark:text-white"
            name={isOpen ? 'chevron-up' : 'chevron-down'}
            size={14}
          />
        </View>
      </Pressable>

      {/* Na web a resposta fica sempre no DOM (escondida quando fechada) para entrar no HTML estático. */}
      {(isOpen || !isNative) && (
        <View className="px-5 pb-5" style={isOpen ? undefined : { display: 'none' }}>
          <ThemedText className="leading-relaxed text-neutral-500">{answer}</ThemedText>
          {link ? (
            <Link href={link.href as Href} asChild>
              <Pressable accessibilityRole="link" className="mt-3 self-start">
                <ThemedText className="font-semibold text-sm text-primary">{link.label}</ThemedText>
              </Pressable>
            </Link>
          ) : null}
        </View>
      )}
    </Card>
  );
};

// Prévia estática do painel — decorativa, não reflete dados reais
const SimulatedApp = () => (
  <View
    className={
      isNative
        ? 'relative w-full self-center'
        : 'relative mx-auto w-full max-w-[460px] lg:max-w-none'
    }
  >
    <View className="pointer-events-none absolute -inset-6 -z-10 rounded-[32px] bg-primary-50" />
    <Card className={`w-full ${isNative ? 'p-4' : 'p-6 sm:p-7'}`}>
      <View className="mb-5 flex-row items-center justify-between">
        <View className="flex-row items-center gap-2">
          <View className="h-9 w-9 items-center justify-center rounded-full bg-primary-50 dark:bg-primary">
            <Ionicons className="text-primary dark:text-white" name="wallet-outline" size={18} />
          </View>
          <View>
            <ThemedText className="font-bold text-sm">Este mês</ThemedText>
            <ThemedText className="text-xs text-neutral-500">18 cobranças ativas</ThemedText>
          </View>
        </View>
      </View>

      <View className="gap-3">
        <View className="flex-row items-center justify-between rounded-2xl border border-neutral-900/5 bg-warning-50 p-4">
          <View className="min-w-0 flex-1 flex-row items-center gap-3">
            <Ionicons className="text-amber-700" name="time-outline" size={22} />
            <View className="min-w-0 flex-1">
              <ThemedText className="font-bold text-sm text-neutral-900">Vence amanhã</ThemedText>
              <ThemedText className="text-xs text-warning-600">Camila Souza</ThemedText>
            </View>
          </View>
          <ThemedText className="ml-2 shrink-0 font-bold text-base" style={tabularNums}>
            R$ 150,00
          </ThemedText>
        </View>

        <View className="flex-row items-center justify-between rounded-2xl border border-neutral-900/5 bg-success-50 p-4">
          <View className="min-w-0 flex-1 flex-row items-center gap-3">
            <Ionicons className="text-success-600" name="checkmark-circle" size={22} />
            <View className="min-w-0 flex-1">
              <ThemedText className="font-bold text-sm text-neutral-900">
                Pagamento confirmado
              </ThemedText>
              <ThemedText className="text-xs text-success-600">Rafael Andrade · agora</ThemedText>
            </View>
          </View>
          <ThemedText
            className="ml-2 shrink-0 font-bold text-base text-success-600"
            style={tabularNums}
          >
            R$ 200,00
          </ThemedText>
        </View>

        <View className="flex-row gap-3 pt-1">
          <View className="flex-1 rounded-2xl border border-neutral-900/5 p-4">
            <ThemedText className="font-medium text-xs text-neutral-500">Recebido no mês</ThemedText>
            <ThemedText className="mt-1 font-bold text-base" style={tabularNums}>
              R$ 3.750,00
            </ThemedText>
          </View>
          <View className="flex-1 rounded-2xl border border-neutral-900/5 p-4">
            <ThemedText className="font-medium text-xs text-neutral-500">A receber</ThemedText>
            <ThemedText className="mt-1 font-bold text-base text-primary" style={tabularNums}>
              R$ 1.250,00
            </ThemedText>
          </View>
        </View>
      </View>
    </Card>
  </View>
);

const PLAN_CONFIG: Record<string, { icon: keyof typeof Ionicons.glyphMap; tagline?: string }> = {
  free: { icon: 'leaf-outline', tagline: 'Comece grátis' },
  basic: { icon: 'rocket-outline', tagline: 'Para começar' },
  professional: { icon: 'star-outline', tagline: 'Recomendado' },
  enterprise: { icon: 'business-outline', tagline: 'Escala' },
  custom: { icon: 'chatbubbles-outline', tagline: 'Sob medida' },
};

/**
 * Equipe não escolhe faixa: monta o plano na calculadora, depois de entrar.
 * Aqui só o "a partir de", tirado da mesma fórmula (ou da cópia de reserva).
 */
function TeamPlanTeaser() {
  const { data } = useTeamPricing();
  const { version } = data ?? FALLBACK_TEAM_PRICING;
  const from = quoteTeamPlan(version, {
    professionals: version.minProfessionals,
    clients: version.blockSize,
  });

  return (
    <View className="relative z-10 mx-auto mt-10 w-full max-w-xl">
      <Card className="gap-4 p-6">
        <View className="flex-row items-center gap-3">
          <View className="h-10 w-10 items-center justify-center rounded-xl bg-primary/10 dark:bg-primary">
            <Ionicons className="text-primary dark:text-white" name="people-outline" size={20} />
          </View>
          <View className="flex-1">
            <ThemedText role="heading" aria-level={3} className="font-bold text-lg">
              Tem equipe?
            </ThemedText>
            <ThemedText className="text-sm text-neutral-500">
              Você escolhe quantos profissionais e clientes por mês, e o preço sai na hora.
            </ThemedText>
          </View>
        </View>
        <ThemedText className="text-sm text-neutral-600" style={tabularNums}>
          A partir de{' '}
          <ThemedText className="font-bold text-sm text-neutral-900" style={tabularNums}>
            {formatCents(from.totalCents)}/mês
          </ThemedText>{' '}
          com {version.minProfessionals} profissionais.
        </ThemedText>
        <Link href="/(auth)/register" asChild>
          <Button variant="outline" size="md">
            <Text className="font-semibold text-neutral-900">Começar com a equipe</Text>
          </Button>
        </Link>
      </Card>
    </View>
  );
}

function PlansSection({
  sectionRefs,
  handleSectionLayout,
}: {
  sectionRefs: React.MutableRefObject<{ [key: string]: View | null }>;
  handleSectionLayout: (id: string) => () => void;
}) {
  const th = useThemedHex();
  // Reserva de `facts.ts` como placeholder: os planos saem no HTML estático e o
  // preço vivo do Stripe entra por cima quando a API responde (sem gravar a
  // reserva no cache que as telas de assinatura também leem).
  const { data: plans } = useQuery({
    queryKey: ['stripe-plans'],
    queryFn: async () => {
      const res = await api.get('/stripe/plans');
      return res.data as HomePlan[];
    },
    placeholderData: FALLBACK_PLANS,
  });

  const paidPlans = (plans?.length ? plans : FALLBACK_PLANS).filter((p) => p.id !== 'custom');
  const displayPlans: HomePlan[] = [FREE_PLAN_CARD, ...paidPlans];
  const comparison = planComparisonTable(
    Object.fromEntries(paidPlans.map((p) => [p.id, p.priceMonthly])),
  );

  return (
    <View
      className={sectionPadding}
      ref={(el) => {
        sectionRefs.current['planos'] = el;
      }}
      nativeID="planos"
      onLayout={handleSectionLayout('planos')}
    >
      <View className="pointer-events-none absolute -left-20 top-[2%] z-0 h-64 w-64 rounded-full bg-primary-50 md:h-[380px] md:w-[380px]" />
      <View className="pointer-events-none absolute -bottom-20 right-[8%] z-0 h-40 w-40 rounded-full bg-secondary-50 md:h-64 md:w-64" />
      <View className="pointer-events-none absolute right-[-30px] top-[38%] z-0 h-20 w-20 rounded-full bg-secondary-50 md:h-32 md:w-32" />

      <View
        className={`relative z-10 mx-auto max-w-2xl items-center ${isNative ? 'mb-8' : 'mb-16'}`}
      >
        <ThemedText
          role="heading"
          aria-level={2}
          className="mb-6 text-balance text-center font-extrabold text-3xl tracking-tight sm:text-4xl lg:text-5xl"
        >
          {PLANS_SECTION.heading}
        </ThemedText>
        <ThemedText className="text-balance text-center text-base text-neutral-500 sm:text-lg">
          {PLANS_SECTION.capsule}
        </ThemedText>
      </View>

      <View
        className={
          Platform.OS === 'web'
            ? 'mx-auto max-w-5xl gap-6'
            : 'mx-auto flex w-full max-w-[320px] flex-col gap-6'
        }
        style={
          Platform.OS === 'web'
            ? {
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                maxWidth: 1024,
                marginLeft: 'auto',
                marginRight: 'auto',
              } as any
            : undefined
        }
      >
        {displayPlans.map((plan) => {
            const config = PLAN_CONFIG[plan.id] ?? { icon: 'ellipse-outline' as const };
            const isHighlight = plan.highlight;

            return (
              <View key={plan.id} className={Platform.OS === 'web' ? 'h-full w-full' : 'w-full'}>
                <Link href="/(auth)/register" asChild>
                  <PressableScale className={Platform.OS === 'web' ? 'h-full' : 'w-full'} haptic>
                    <Card
                      className={`p-6${Platform.OS === 'web' ? ' h-full' : ''} ${
                        isHighlight ? 'border-2 border-primary shadow-lg' : ''
                      }`}
                    >
                      {config.tagline && (
                        <Badge
                          variant={isHighlight ? 'default' : 'primary'}
                          className={`mb-4 self-start ${isHighlight ? 'bg-primary' : ''}`}
                        >
                          <View className="flex-row items-center gap-1.5">
                            <Ionicons
                              name={config.icon as any}
                              size={13}
                              color={isHighlight ? '#fff' : '#FD5001'}
                            />
                            <Text
                              className={`font-semibold text-xs ${isHighlight ? 'text-white' : 'text-primary-700'}`}
                            >
                              {config.tagline}
                            </Text>
                          </View>
                        </Badge>
                      )}
                      <ThemedText role="heading" aria-level={3} className="mb-2 font-bold text-xl">
                        {plan.name}
                      </ThemedText>
                      <ThemedText className="mb-3 text-sm leading-snug text-neutral-500">
                        {plan.description}
                      </ThemedText>
                      {plan.capacity && (
                        <Badge variant="primary" className="mb-4 self-start">
                          <View className="flex-row items-center gap-1.5">
                            <Ionicons name="people-outline" size={13} color="#FD5001" />
                            <Text className="font-semibold text-xs text-primary-700">
                              {plan.capacity}
                            </Text>
                          </View>
                        </Badge>
                      )}
                      {plan.priceMonthly >= 0 ? (
                        <View className="mb-4 flex-row items-baseline gap-1">
                          <ThemedText className="font-extrabold text-3xl" style={tabularNums}>
                            R${' '}
                            {plan.priceMonthly.toLocaleString('pt-BR', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </ThemedText>
                          <ThemedText className="text-base text-neutral-400">/mês</ThemedText>
                        </View>
                      ) : null}
                      <View className="mb-2 gap-2">
                        {plan.features.map((feature) => (
                          <View key={feature} className="flex-row items-start gap-2">
                            <Ionicons
                              className="mt-0.5 text-primary"
                              name="checkmark"
                              size={14}
                            />
                            <ThemedText className="flex-1 text-sm leading-snug text-neutral-600">
                              {feature}
                            </ThemedText>
                          </View>
                        ))}
                      </View>
                      <View className="mt-auto pt-4">
                        <Button
                          size="md"
                          className="w-full"
                          variant={isHighlight ? 'default' : 'outline'}
                        >
                          <Text
                            className={`font-semibold text-base ${isHighlight ? 'text-white' : 'text-neutral-900'}`}
                          >
                            {plan.priceMonthly > 0 ? `Assinar ${plan.name}` : 'Criar conta grátis'}
                          </Text>
                          <Ionicons
                            name="arrow-forward"
                            size={16}
                            color={isHighlight ? '#fff' : th('#18181B')}
                          />
                        </Button>
                      </View>
                    </Card>
                  </PressableScale>
                </Link>
              </View>
            );
          })}
      </View>

      <View className="relative z-10 mx-auto mt-10 w-full max-w-5xl">
        <SemanticTable
          caption={PLANS_SECTION.tableCaption}
          showCaption
          columns={comparison.columns}
          rows={comparison.rows}
        />
      </View>

      <TeamPlanTeaser />

      <View className="relative z-10 mx-auto mt-10 max-w-xl items-center">
        <ThemedText className="mb-1 text-center font-semibold text-base">
          {CUSTOM_PLAN_BLOCK.heading}
        </ThemedText>
        <ThemedText className="mb-4 text-center text-sm text-neutral-500">
          {CUSTOM_PLAN_BLOCK.body}
        </ThemedText>
        <Link href="/suporte" asChild>
          <Button variant="outline" size="md">
            <Text className="font-semibold text-neutral-900">Falar com a equipe</Text>
            <Ionicons className="text-neutral-900" name="arrow-forward" size={16} />
          </Button>
        </Link>
      </View>
    </View>
  );
}

const WA_HEADER = '#075E54';
const WA_WALLPAPER = '#ECE5DD';
const WA_BUBBLE_OUT = '#DCF8C6';
const WA_TICK = '#34B7F1';

function ChatBubble({ text, outgoing, time }: ChatMessage) {
  return (
    <View className={`max-w-[80%] ${outgoing ? 'self-end' : 'self-start'}`}>
      <View
        className={`rounded-2xl px-3.5 py-2.5 ${
          outgoing ? 'rounded-tr-sm' : 'rounded-tl-sm border border-neutral-900/5 bg-card'
        }`}
        style={[
          { borderCurve: 'continuous' } as any,
          outgoing ? { backgroundColor: WA_BUBBLE_OUT } : null,
        ]}
      >
        <Text className="text-sm leading-snug text-neutral-800">{text}</Text>
        <View className="mt-1 flex-row items-center justify-end gap-1">
          <Text className="text-xs text-neutral-400">{time}</Text>
          {outgoing && <Ionicons name="checkmark-done" size={13} color={WA_TICK} />}
        </View>
      </View>
    </View>
  );
}

function ChatMock({
  contact,
  messages,
  delayStart = 0,
}: {
  contact: string;
  messages: ChatMessage[];
  delayStart?: number;
}) {
  return (
    <View
      className="w-full max-w-[380px] self-center overflow-hidden rounded-[32px] border border-neutral-900/10 bg-card shadow-2xl"
      style={{ borderCurve: 'continuous' } as any}
    >
      <View
        className="flex-row items-center gap-3 px-4 py-3"
        style={{ backgroundColor: WA_HEADER }}
      >
        <View className="h-9 w-9 items-center justify-center rounded-full bg-white/20">
          <Ionicons name="person" size={18} color="#fff" />
        </View>
        <View>
          <Text className="font-semibold text-sm text-white">{contact}</Text>
          <Text className="text-xs text-white/70">online</Text>
        </View>
        <View className="ml-auto flex-row gap-4">
          <Ionicons name="videocam-outline" size={18} color="#fff" />
          <Ionicons name="call-outline" size={16} color="#fff" />
        </View>
      </View>
      <View className="gap-2 px-3 py-4" style={{ backgroundColor: WA_WALLPAPER }}>
        {messages.map((m, i) => (
          <SlideInView
            key={i}
            delay={delayStart + i * 260}
            direction={m.outgoing ? 'right' : 'left'}
            distance={18}
          >
            <ChatBubble {...m} />
          </SlideInView>
        ))}
      </View>
    </View>
  );
}

const scrollThreshold = 400;

export default function HomeScreen() {
  const insets = useSafeAreaInsets();
  const scrollViewRef = useRef<ScrollView | null>(null);
  const sectionRefs = useRef<{ [key: string]: View | null }>({});
  const sectionPositions = useRef<{ [key: string]: number }>({});
  const [showScrollButton, setShowScrollButton] = useState(false);

  useEffect(() => {
    if (Platform.OS === 'web') {
      captureAttributionFromUrl();
    }
  }, []);

  const handleScroll = (e: NativeSyntheticEvent<NativeScrollEvent>) => {
    const y = e.nativeEvent.contentOffset.y;
    setShowScrollButton(y > scrollThreshold);
  };

  useEffect(() => {
    if (Platform.OS === 'web') {
      const onScroll = () => {
        setShowScrollButton(
          (window.scrollY || document.documentElement.scrollTop) > scrollThreshold,
        );
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      return () => window.removeEventListener('scroll', onScroll);
    }
  }, []);

  const handleScrollToTop = () => {
    if (Platform.OS === 'web') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    scrollViewRef.current?.scrollTo({ y: 0, animated: true });
  };

  const handleSectionLayout = (id: string) => () => {
    const targetRef = sectionRefs.current[id];
    const scrollNode = scrollViewRef.current;
    if (!targetRef || !scrollNode) return;

    if (Platform.OS === 'web') {
      const targetElement = targetRef as unknown as HTMLElement;
      let scrollElement = scrollNode as any;
      if (scrollElement?.getScrollableNode) {
        scrollElement = scrollElement.getScrollableNode(); // Animated.ScrollView wrapper
      }

      if (
        targetElement &&
        scrollElement &&
        targetElement.getBoundingClientRect &&
        scrollElement.getBoundingClientRect
      ) {
        const targetRect = targetElement.getBoundingClientRect();
        const scrollRect = scrollElement.getBoundingClientRect?.() || { top: 0 };
        const scrollTop = scrollElement.scrollTop || 0;
        const relativeY = targetRect.top - scrollRect.top + scrollTop;
        sectionPositions.current[id] = relativeY;
      }
    } else {
      const nodeHandle = findNodeHandle(scrollNode);
      const targetHandle = findNodeHandle(targetRef);
      if (nodeHandle && targetHandle) {
        (UIManager as any).measureLayout(
          targetHandle,
          nodeHandle,
          () => {
            console.warn(`Could not measure layout for section: ${id}`);
          },
          (x: number, y: number, width: number, height: number) => {
            sectionPositions.current[id] = y;
          },
        );
      }
    }
  };

  const handleNavigate = (id: string) => {
    const scrollNode = scrollViewRef.current;
    const targetRef = sectionRefs.current[id];
    if (!scrollNode || !targetRef) return;

    const HEADER_OFFSET = 80;

    if (Platform.OS !== 'web') {
      const storedPosition = sectionPositions.current[id];
      if (storedPosition !== undefined) {
        scrollNode.scrollTo({
          y: Math.max(0, storedPosition - HEADER_OFFSET),
          animated: true,
        });
        return;
      }
    }

    if (Platform.OS === 'web') {
      const targetElement = targetRef as unknown as HTMLElement;
      let scrollElement = scrollNode as any;
      if (scrollElement?.getScrollableNode) {
        scrollElement = scrollElement.getScrollableNode();
      }

      if (targetElement) {
        const targetRect = targetElement.getBoundingClientRect();
        const currentScroll =
          window.scrollY || document.documentElement.scrollTop || scrollElement?.scrollTop || 0;
        const targetY = currentScroll + targetRect.top - HEADER_OFFSET;

        window.scrollTo({ top: targetY, behavior: 'smooth' });
        // Try scrolling the element too, just in case
        if (scrollNode.scrollTo) {
          scrollNode.scrollTo({ y: targetY });
        }
      }
    } else {
      const nodeHandle = findNodeHandle(scrollNode);
      const targetHandle = findNodeHandle(targetRef);
      if (nodeHandle && targetHandle) {
        (UIManager as any).measureLayout(
          targetHandle,
          nodeHandle,
          () => {},
          (x: number, y: number, width: number, height: number) => {
            scrollNode.scrollTo({
              y: Math.max(0, y - HEADER_OFFSET),
              animated: true,
            });
          },
        );
      }
    }
  };

  return (
    <View className="flex-1 bg-background">
      <Stack.Screen options={{ headerShown: false, title: 'Controlapag' }} />
      <SeoHead path="/" jsonLd={[SOFTWARE_APPLICATION_JSON_LD, faqJsonLd(FAQ_ITEMS)]} />
      <SiteHeader onNavigate={handleNavigate} />

      <ScrollView
        keyboardShouldPersistTaps="handled"
        ref={scrollViewRef}
        className="flex-1"
        contentContainerStyle={{ paddingBottom: Platform.OS !== 'web' ? insets.bottom + 40 : 40 }}
        onScroll={handleScroll}
        scrollEventThrottle={16}
        showsVerticalScrollIndicator={true}
        nestedScrollEnabled={true}
      >
        <AppConfigErrorBanner />
        <View className={heroPadding}>
          <View className="pointer-events-none absolute left-[-100px] top-[-80px] z-0 h-[250px] w-[250px] rounded-full bg-primary-50 md:h-[400px] md:w-[400px]" />
          <View className="pointer-events-none absolute bottom-[-100px] right-[-120px] z-0 h-[300px] w-[300px] rounded-full bg-secondary-50 md:h-[500px] md:w-[500px]" />

          <View className="relative z-10 mx-auto max-w-4xl items-center">
            <FadeInView disableOnWeb className="items-center">
              <Badge variant="primary" className="mb-6">
                <Text className="font-semibold text-xs text-primary-700">
                  {HOME_HERO.eyebrow}
                </Text>
              </Badge>
            </FadeInView>

            <FadeInView disableOnWeb delay={60} className="items-center">
              <ThemedText
                role="heading"
                aria-level={1}
                className="mb-6 max-w-3xl text-balance text-center font-extrabold text-4xl leading-tight tracking-tight sm:text-5xl lg:text-6xl"
              >
                {HOME_HERO.h1}
              </ThemedText>
            </FadeInView>

            <FadeInView disableOnWeb delay={120} className="items-center">
              <ThemedText className="mx-auto mb-10 max-w-2xl text-balance text-center text-base leading-relaxed text-neutral-500 sm:text-lg lg:text-xl">
                {hidePlansOnIOS ? HOME_HERO.answerNoPrice : HOME_HERO.answer}
              </ThemedText>
            </FadeInView>

            <FadeInView disableOnWeb delay={180} className="mb-12 w-full">
              <View className="w-full flex-col justify-center gap-4 sm:flex-row sm:items-center">
                <Link href="/(auth)/register" asChild>
                  <Button
                    size="lg"
                    className="w-full min-w-[200px] sm:w-auto"
                    title="Criar conta grátis"
                  />
                </Link>
                <Button
                  size="lg"
                  variant="outline"
                  className="w-full min-w-[200px] sm:w-auto"
                  onPress={() => handleNavigate('funcionalidades')}
                >
                  <Text className="font-semibold text-base text-neutral-900">
                    Ver como funciona
                  </Text>
                  <Ionicons className="text-neutral-900" name="arrow-forward" size={18} />
                </Button>
              </View>
            </FadeInView>

            <FadeInView disableOnWeb delay={240} className="w-full items-center">
              <View
                className={
                  isNative
                    ? 'mx-auto flex max-w-4xl flex-col items-center gap-3'
                    : 'mx-auto flex max-w-4xl flex-row flex-wrap items-center justify-center gap-x-8 gap-y-3'
                }
              >
                {TRUST_BADGES.map((badge) => (
                  <View key={badge.label} className="flex-row items-center gap-2">
                    <Ionicons name={badge.icon as any} size={16} color="#FD5001" />
                    <ThemedText className="text-sm text-neutral-500">{badge.label}</ThemedText>
                  </View>
                ))}
              </View>
            </FadeInView>
          </View>
        </View>

        <View className="relative px-4 py-12 sm:py-20">
          <View className="pointer-events-none absolute -left-24 -top-16 h-64 w-64 rounded-full bg-primary-50 md:h-96 md:w-96" />
          <View className="pointer-events-none absolute bottom-[-30px] right-[6%] h-28 w-28 rounded-full bg-secondary-50 md:h-48 md:w-48" />
          <View className="pointer-events-none absolute right-[28%] top-[8%] h-16 w-16 rounded-full bg-secondary-50 md:h-24 md:w-24" />

          <View
            className={
              isNative
                ? 'relative mx-auto flex w-full max-w-6xl flex-col items-center gap-10'
                : 'relative mx-auto flex w-full max-w-6xl flex-col items-center gap-14 lg:flex-row lg:items-center lg:justify-between'
            }
          >
            <View className={isNative ? 'w-full items-center' : 'w-full max-w-xl lg:items-start'}>
              <FadeInView disableOnWeb className={isNative ? 'items-center' : undefined}>
                <Badge variant="primary" className="mb-6">
                  <Text className="font-semibold text-xs text-primary-700">
                    {WHATSAPP_SECTION.badge}
                  </Text>
                </Badge>
              </FadeInView>

              <FadeInView disableOnWeb delay={60} className={isNative ? 'items-center' : undefined}>
                <ThemedText
                  role="heading"
                  aria-level={2}
                  className={`mb-6 text-balance font-extrabold text-4xl leading-tight tracking-tight sm:text-5xl lg:text-6xl ${
                    isNative ? 'text-center' : 'text-center lg:text-left'
                  }`}
                >
                  {WHATSAPP_SECTION.heading}
                </ThemedText>
              </FadeInView>

              <FadeInView
                disableOnWeb
                delay={120}
                className={isNative ? 'items-center' : undefined}
              >
                <ThemedText
                  className={`mb-8 max-w-lg text-balance text-base leading-relaxed text-neutral-500 sm:text-lg ${
                    isNative ? 'text-center' : 'text-center lg:text-left'
                  }`}
                >
                  {WHATSAPP_SECTION.capsule} {WHATSAPP_SECTION.body}
                </ThemedText>
              </FadeInView>

              <FadeInView disableOnWeb delay={180} className="w-full">
                <Button
                  size="md"
                  className="w-full"
                  onPress={() => router.push('/(auth)/register')}
                >
                  <Text className="font-semibold text-base text-white">Criar conta grátis</Text>
                  <Ionicons name="arrow-forward" size={16} color="#fff" />
                </Button>
              </FadeInView>
            </View>

            <View className="w-full max-w-[380px]">
              <SlideInView disableOnWeb direction="up" delay={120} distance={24}>
                <ChatMock contact={REMINDER_CHAT_CONTACT} messages={REMINDER_CHAT} delayStart={400} />
              </SlideInView>
            </View>
          </View>
        </View>

        <View className={sectionPadding}>
          <View className="pointer-events-none absolute -right-20 top-[4%] z-0 h-56 w-56 rounded-full bg-secondary-50 md:h-[350px] md:w-[350px]" />
          <View className="pointer-events-none absolute -bottom-16 left-[10%] z-0 h-24 w-24 rounded-full bg-primary-50 md:h-40 md:w-40" />

          <View
            className={`relative z-10 mx-auto max-w-2xl items-center ${isNative ? 'mb-8' : 'mb-12'}`}
          >
            <Badge variant="primary" className="mb-6">
              <Text className="font-semibold text-xs text-primary-700">{SEGMENTS_SECTION.badge}</Text>
            </Badge>
            <ThemedText
              role="heading"
              aria-level={2}
              className="mb-6 text-balance text-center font-extrabold text-3xl tracking-tight sm:text-4xl lg:text-5xl"
            >
              {SEGMENTS_SECTION.heading}
            </ThemedText>
            <ThemedText className="text-balance text-center text-base text-neutral-500 sm:text-lg">
              {SEGMENTS_SECTION.capsule}
            </ThemedText>
          </View>

          <View
            className={
              Platform.OS === 'web'
                ? 'relative mx-auto grid w-full max-w-5xl grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4'
                : 'relative mx-auto flex w-full max-w-5xl flex-col gap-3'
            }
          >
            {SEGMENT_PAGES.map((segment) => (
              <Link key={segment.slug} href={`/para/${segment.slug}` as Href} asChild>
                <PressableScale accessibilityRole="link" className="w-full">
                  <Card className="flex-row items-center justify-between gap-3 px-5 py-4">
                    <ThemedText className="flex-1 font-semibold text-base">{segment.label}</ThemedText>
                    <Ionicons className="text-primary" name="arrow-forward" size={16} />
                  </Card>
                </PressableScale>
              </Link>
            ))}
          </View>

          {homeGuides.length ? (
            <View className={`relative z-10 mx-auto w-full max-w-5xl ${isNative ? 'mt-12' : 'mt-20'}`}>
              <ThemedText
                role="heading"
                aria-level={2}
                className="mb-3 text-balance text-center font-extrabold text-2xl tracking-tight sm:text-3xl"
              >
                {GUIDES_SECTION.heading}
              </ThemedText>
              <ThemedText className="mb-8 text-balance text-center text-base text-neutral-500">
                {GUIDES_SECTION.capsule}
              </ThemedText>
              <View
                className={
                  Platform.OS === 'web'
                    ? 'grid grid-cols-1 gap-4 md:grid-cols-2'
                    : 'flex flex-col gap-3'
                }
              >
                {homeGuides.map((guide) => (
                  <Link key={guide.slug} href={`/guias/${guide.slug}` as Href} asChild>
                    <PressableScale accessibilityRole="link" className="w-full">
                      <Card className="flex-row items-center justify-between gap-3 px-5 py-4">
                        <ThemedText className="flex-1 font-semibold text-base">{guide.h1}</ThemedText>
                        <Ionicons className="text-primary" name="arrow-forward" size={16} />
                      </Card>
                    </PressableScale>
                  </Link>
                ))}
              </View>
              <Link href={'/guias' as Href} asChild>
                <Pressable accessibilityRole="link" className="mt-6 self-center">
                  <ThemedText className="font-semibold text-sm text-primary">
                    {GUIDES_SECTION.allLabel}
                  </ThemedText>
                </Pressable>
              </Link>
            </View>
          ) : null}
        </View>

        <View className={sectionPadding}>
          <View className="pointer-events-none absolute -bottom-10 left-[4%] z-0 h-40 w-40 rounded-full bg-primary-50 md:h-64 md:w-64" />
          <View className="pointer-events-none absolute right-[12%] top-[-20px] z-0 h-20 w-20 rounded-full bg-secondary-50 md:h-32 md:w-32" />

          <View className="relative z-10 mx-auto max-w-3xl items-center">
            <FadeInView disableOnWeb className="items-center">
              <ThemedText className="text-balance text-center font-bold text-3xl leading-snug tracking-tight text-neutral-900 sm:text-4xl lg:text-5xl">
                {QUOTE.lead}{'\n'}
                <ThemedText className="font-bold text-3xl leading-snug tracking-tight text-primary sm:text-4xl lg:text-5xl">
                  {QUOTE.highlight}
                </ThemedText>
              </ThemedText>
            </FadeInView>
          </View>
        </View>

        <View
          className={sectionPadding}
          ref={(el) => {
            sectionRefs.current['funcionalidades'] = el;
          }}
          nativeID="funcionalidades"
          onLayout={handleSectionLayout('funcionalidades')}
        >
          <View className="pointer-events-none absolute -bottom-32 left-[2%] z-0 h-64 w-64 rounded-full bg-secondary-50 md:h-[420px] md:w-[420px]" />
          <View className="pointer-events-none absolute -top-10 right-[18%] z-0 h-20 w-20 rounded-full bg-primary-50 md:h-32 md:w-32" />
          <View className="pointer-events-none absolute right-[-40px] top-[45%] z-0 h-32 w-32 rounded-full bg-primary-50 md:h-52 md:w-52" />

          <View
            className={`relative z-10 mx-auto max-w-2xl items-center ${isNative ? 'mb-8' : 'mb-16'}`}
          >
            <ThemedText
              role="heading"
              aria-level={2}
              className="mb-6 text-balance text-center font-extrabold text-3xl tracking-tight sm:text-4xl lg:text-5xl"
            >
              {FEATURES_SECTION.heading}
            </ThemedText>
            <ThemedText className="text-balance text-center text-base text-neutral-500 sm:text-lg">
              {FEATURES_SECTION.capsule}
            </ThemedText>
          </View>

          <View
            className={
              Platform.OS === 'web'
                ? 'relative mx-auto grid max-w-7xl grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3'
                : 'relative mx-auto flex max-w-7xl flex-col gap-6'
            }
          >
            {FEATURES.map((feature, index) => (
              <FadeInView disableOnWeb key={feature.title} delay={index * 60}>
                <Card className="p-8">
                  <View className="mb-6 h-14 w-14 items-center justify-center rounded-2xl bg-primary-50 dark:bg-primary">
                    <Ionicons
                      className="text-primary dark:text-white"
                      name={feature.icon as any}
                      size={26}
                    />
                  </View>
                  <ThemedText role="heading" aria-level={3} className="mb-3 font-bold text-xl">
                    {feature.title}
                  </ThemedText>
                  <ThemedText className="leading-relaxed text-neutral-500">
                    {feature.description}
                  </ThemedText>
                  {feature.link ? (
                    <Link href={feature.link.href as Href} asChild>
                      <Pressable accessibilityRole="link" className="mt-4 self-start">
                        <ThemedText className="font-semibold text-sm text-primary">
                          {feature.link.label}
                        </ThemedText>
                      </Pressable>
                    </Link>
                  ) : null}
                </Card>
              </FadeInView>
            ))}
          </View>
        </View>

        <View
          className={sectionPadding}
          ref={(el) => {
            sectionRefs.current['beneficios'] = el;
          }}
          nativeID="beneficios"
          onLayout={handleSectionLayout('beneficios')}
        >
          <View className="pointer-events-none absolute -right-16 top-[6%] z-0 h-56 w-56 rounded-full bg-primary-50 md:h-96 md:w-96" />
          <View className="pointer-events-none absolute bottom-[-24px] left-[8%] z-0 h-28 w-28 rounded-full bg-secondary-50 md:h-44 md:w-44" />
          <View className="pointer-events-none absolute left-[-30px] top-[60%] z-0 h-16 w-16 rounded-full bg-secondary-50 md:h-24 md:w-24" />

          <View
            className={`relative z-10 mx-auto w-full max-w-7xl ${isNative ? 'flex flex-col items-stretch gap-8' : 'grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-20'}`}
          >
            <View>
              <ThemedText
                role="heading"
                aria-level={2}
                className="mb-6 text-balance font-extrabold text-3xl tracking-tight sm:text-5xl"
              >
                {PANEL_SECTION.heading}
              </ThemedText>
              <ThemedText className="mb-8 text-lg leading-relaxed text-neutral-500">
                {PANEL_SECTION.capsule}
              </ThemedText>

              <View className="grid gap-4 sm:grid-cols-2">
                {BENEFITS.map((benefit, index) => (
                  <View key={index} className="flex flex-row items-start gap-3">
                    <View className="mt-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-primary-50 dark:bg-primary">
                      <Ionicons
                        className="text-primary dark:text-white"
                        name="checkmark"
                        size={12}
                      />
                    </View>
                    <ThemedText className="flex-1 font-medium text-sm leading-relaxed text-neutral-700">
                      {benefit}
                    </ThemedText>
                  </View>
                ))}
              </View>
            </View>

            <SimulatedApp />
          </View>
        </View>

        <View
          className={sectionPadding}
          ref={(el) => {
            sectionRefs.current['como-funciona'] = el;
          }}
          nativeID="como-funciona"
          onLayout={handleSectionLayout('como-funciona')}
        >
          <View className="pointer-events-none absolute -bottom-16 right-[10%] z-0 h-48 w-48 rounded-full bg-secondary-50 md:h-72 md:w-72" />
          <View className="pointer-events-none absolute -top-8 left-[6%] z-0 h-20 w-20 rounded-full bg-primary-50 md:h-32 md:w-32" />
          <View className="pointer-events-none absolute bottom-[-10px] right-[35%] z-0 h-14 w-14 rounded-full bg-primary-50 md:h-20 md:w-20" />

          <View
            className={`relative z-10 mx-auto max-w-2xl items-center ${isNative ? 'mb-8' : 'mb-16'}`}
          >
            <ThemedText
              role="heading"
              aria-level={2}
              className="mb-4 text-balance text-center font-extrabold text-3xl tracking-tight sm:text-4xl"
            >
              {HOW_IT_WORKS_SECTION.heading}
            </ThemedText>
            <ThemedText className="text-balance text-center text-base text-neutral-500 sm:text-lg">
              {HOW_IT_WORKS_SECTION.capsule}
            </ThemedText>
          </View>

          <View className="relative mx-auto grid max-w-5xl grid-cols-1 gap-8 md:grid-cols-3">
            {HOW_IT_WORKS.map((step, index) => (
              <FadeInView disableOnWeb key={step.step} delay={index * 100} className="items-center">
                <View className="relative flex flex-col items-center text-center">
                  <View className="relative mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-primary">
                    <ThemedText className="font-black text-3xl text-white">{step.step}</ThemedText>
                  </View>
                  <ThemedText
                    role="heading"
                    aria-level={3}
                    className="mb-3 text-center font-bold text-xl"
                  >
                    {step.title}
                  </ThemedText>
                  <ThemedText className="text-center leading-relaxed text-neutral-500">
                    {step.description}
                  </ThemedText>
                </View>
              </FadeInView>
            ))}
          </View>
        </View>

        {!hidePlansOnIOS && (
          <PlansSection sectionRefs={sectionRefs} handleSectionLayout={handleSectionLayout} />
        )}

        <View className={sectionPadding}>
          <View className="pointer-events-none absolute -right-16 bottom-[-8%] z-0 h-40 w-40 rounded-full bg-secondary-50 md:h-64 md:w-64" />
          <View className="pointer-events-none absolute -top-10 left-[14%] z-0 h-24 w-24 rounded-full bg-primary-50 md:h-40 md:w-40" />
          <View className="pointer-events-none absolute bottom-[6%] left-[32%] z-0 h-12 w-12 rounded-full bg-primary-50 md:h-16 md:w-16" />

          <View
            className={`relative z-10 mx-auto max-w-3xl items-center ${isNative ? 'mb-8' : 'mb-12'}`}
          >
            <ThemedText
              role="heading"
              aria-level={2}
              className="mb-4 text-balance text-center font-extrabold text-3xl tracking-tight sm:text-4xl"
            >
              {FAQ_SECTION.heading}
            </ThemedText>
            <ThemedText className="text-balance text-center text-base text-neutral-500 sm:text-lg">
              {FAQ_SECTION.capsule}
            </ThemedText>
          </View>

          <View className="mx-auto w-full max-w-3xl gap-3">
            {FAQ_ITEMS.map((item) => (
              <FaqItem key={item.question} {...item} />
            ))}
          </View>
        </View>

        <View className={sectionPadding}>
          <View className="pointer-events-none absolute -left-10 top-[10%] z-0 h-24 w-24 rounded-full bg-primary-50 md:h-40 md:w-40" />
          <View className="pointer-events-none absolute -right-6 bottom-[6%] z-0 h-16 w-16 rounded-full bg-secondary-50 md:h-28 md:w-28" />

          <View
            className={`relative z-10 mx-auto w-full max-w-5xl overflow-hidden rounded-2xl bg-primary px-6 text-center shadow-2xl sm:px-12 ${isNative ? 'py-10' : 'py-16 md:py-24'}`}
            style={{ borderCurve: 'continuous' } as any}
          >
            <View className="pointer-events-none absolute left-0 top-0 h-[220px] w-[220px] -translate-x-1/3 -translate-y-1/3 rounded-full bg-white/10" />
            <View className="pointer-events-none absolute bottom-0 right-0 h-[340px] w-[340px] translate-x-1/3 translate-y-1/3 rounded-full bg-white/10" />
            <View className="pointer-events-none absolute right-[15%] top-0 h-16 w-16 -translate-y-1/2 rounded-full bg-white/10" />

            <View className="relative z-10 mx-auto w-full max-w-3xl items-center">
              <ThemedText
                role="heading"
                aria-level={2}
                className="mb-6 text-balance text-center font-extrabold text-3xl tracking-tight text-white sm:text-5xl"
              >
                {FINAL_CTA.heading}
              </ThemedText>
              <ThemedText className="mx-auto mb-10 max-w-2xl text-balance text-center text-lg text-white/90 sm:text-xl">
                {hidePlansOnIOS ? FINAL_CTA.bodyNoPrice : FINAL_CTA.body}
              </ThemedText>

              <View className="w-full flex-col justify-center gap-4 sm:flex-row sm:items-center">
                <Link href="/(auth)/register" asChild>
                  <Button
                    size="lg"
                    variant="white"
                    className="w-full sm:w-auto"
                    title="Criar conta grátis"
                  />
                </Link>
                <Link href="/(auth)/login" asChild>
                  <Button
                    size="lg"
                    variant="outline"
                    className="w-full border-2 border-white/60 bg-transparent sm:w-auto"
                  >
                    <Text className="font-semibold text-base text-white">Já tenho conta</Text>
                  </Button>
                </Link>
              </View>
            </View>
          </View>
        </View>

        <SiteFooter onNavigate={handleNavigate} />

        <View className="h-20 md:hidden" />
      </ScrollView>

      {showScrollButton && (
        <View
          className="absolute bottom-0 left-0 right-0 z-[90] flex-row items-center justify-between gap-4 border-t border-neutral-900/5 bg-background px-4 py-3 shadow-2xl md:hidden"
          style={{ paddingBottom: (isNative ? insets.bottom : 0) + 12 }}
        >
          <ThemedText className="flex-1 font-semibold text-sm" numberOfLines={2}>
            {FINAL_CTA.sticky}
          </ThemedText>
          <Link href="/(auth)/register" asChild>
            <Button size="sm" title="Criar conta grátis" />
          </Link>
        </View>
      )}

      {showScrollButton && (
        <View
          style={{ position: 'absolute', bottom: 32, right: 32, zIndex: 100 }}
          className="hidden md:flex"
        >
          <PressableScale
            onPress={handleScrollToTop}
            haptic
            className="h-12 w-12 items-center justify-center rounded-full bg-primary shadow-lg md:h-14 md:w-14"
          >
            <Ionicons name="arrow-up-outline" size={24} color="#FFFFFF" />
          </PressableScale>
        </View>
      )}
    </View>
  );
}
